import { Injectable } from '@angular/core';
import { IFilterConfig, IFilterRule } from './interfaces';

@Injectable({
    providedIn: 'root'
})
export class FilterService {

    /**
     * Apply filters to data array
     */
    applyFilters<T>(data: T[], filters: IFilterRule[]): T[] {
        if (!filters || filters.length === 0) {
            return data;
        }
        const activeFilters = filters.filter(f => f.enabled);
        if (activeFilters.length === 0) {
            return data;
        }
        return data.filter(item => {
            const matched = activeFilters.some(filter => {
                const result = this.matchesFilter(item, filter);
                return result;
            });
            return matched;
        });
    }

    /**
     * Check if an item matches a filter rule
     */
    private matchesFilter<T>(item: T, filter: IFilterRule): boolean {
        const value = this.getNestedValue(item, filter.columnName);

        switch (filter.filterType) {
            case 'boolean':
                return this.matchesBoolean(value, filter.value);
            
            case 'date':
                return this.matchesDate(value, filter.value);
            
            case 'dateRange':
                return this.matchesDateRange(value, filter.dateFrom, filter.dateTo);
            
            case 'text':
                return this.matchesText(value, filter.value, filter.operator);
            
            case 'number':
                return this.matchesNumber(value, filter.value, filter.operator);
            
            case 'dropdown':
                return this.matchesDropdown(value, filter.value);
            
            default:
                return true;
        }
    }

    /**
     * Boolean filter matching
     */
    private matchesBoolean(value: any, filterValue: boolean): boolean {
        if (filterValue === null || filterValue === undefined) {
            return true;
        }
        
        // Convert value to boolean
        const boolValue = this.toBoolean(value);
        return boolValue === filterValue;
    }

    /**
     * Date filter matching (exact date)
     */
    private matchesDate(value: any, filterValue: Date | string | null): boolean {
        if (!filterValue) {
            return true;
        }

        const itemDate = this.toDate(value);
        const filterDate = this.toDate(filterValue);

        if (!itemDate || !filterDate) {
            return false;
        }

        // Compare only the date part (ignore time)
        return this.isSameDate(itemDate, filterDate);
    }

    /**
     * Date range filter matching
     */
    private matchesDateRange(value: any, dateFrom: Date | null, dateTo: Date | null): boolean {
        const itemDate = this.toDate(value);

        if (!itemDate) {
            return false;
        }

        // Normalize item date to midnight UTC for comparison
        const normalizedItemDate = new Date(Date.UTC(
            itemDate.getUTCFullYear(),
            itemDate.getUTCMonth(),
            itemDate.getUTCDate()
        ));

        if (dateFrom) {
            const normalizedFromDate = this.toDate(dateFrom);
            if (normalizedFromDate) {
                const fromDateMidnight = new Date(Date.UTC(
                    normalizedFromDate.getUTCFullYear(),
                    normalizedFromDate.getUTCMonth(),
                    normalizedFromDate.getUTCDate()
                ));

                if (normalizedItemDate < fromDateMidnight) {
                    return false;
                }
            }
        }

        if (dateTo) {
            const normalizedToDate = this.toDate(dateTo);
            if (normalizedToDate) {
                const toDateMidnight = new Date(Date.UTC(
                    normalizedToDate.getUTCFullYear(),
                    normalizedToDate.getUTCMonth(),
                    normalizedToDate.getUTCDate()
                ));

                if (normalizedItemDate > toDateMidnight) {
                    return false;
                }
            }
        }

        return true;
    }

    /**
     * Text filter matching
     */
    private matchesText(value: any, filterValue: string, operator: string = 'contains'): boolean {
        if (!filterValue || filterValue.trim() === '') {
            return true;
        }

        const itemValue = (value?.toString() || '').toLowerCase();
        const searchValue = filterValue.toLowerCase();

        switch (operator) {
            case 'equals':
                return itemValue === searchValue;
            case 'contains':
                return itemValue.includes(searchValue);
            case 'startsWith':
                return itemValue.startsWith(searchValue);
            case 'endsWith':
                return itemValue.endsWith(searchValue);
            default:
                return itemValue.includes(searchValue);
        }
    }

    /**
     * Number filter matching
     */
    private matchesNumber(value: any, filterValue: number, operator: string = 'equals'): boolean {
        if (filterValue === null || filterValue === undefined) {
            return true;
        }

        const itemValue = parseFloat(value);
        if (isNaN(itemValue)) {
            return false;
        }

        switch (operator) {
            case 'equals':
                return itemValue === filterValue;
            case 'greaterThan':
                return itemValue > filterValue;
            case 'lessThan':
                return itemValue < filterValue;
            default:
                return itemValue === filterValue;
        }
    }

    /**
     * Dropdown filter matching
     */
    private matchesDropdown(value: any, filterValue: any): boolean {
        if (filterValue === null || filterValue === undefined) {
            return true;
        }

        return value === filterValue;
    }

    /**
     * Get nested property value from object
     */
    private getNestedValue(obj: any, path: string): any {
        return path.split('.').reduce((current, prop) => current?.[prop], obj);
    }

    /**
     * Convert value to boolean
     */
    private toBoolean(value: any): boolean {
        if (typeof value === 'boolean') {
            return value;
        }
        
        if (typeof value === 'string') {
            const lower = value.toLowerCase();
            return lower === 'true' || lower === '1' || lower === 'yes' || lower === 'active';
        }
        
        if (typeof value === 'number') {
            return value !== 0;
        }
        
        return !!value;
    }

    /**
     * Convert value to Date object
     */
    private toDate(value: any): Date | null {
        if (!value) {
            return null;
        }

        if (value instanceof Date) {
            return value;
        }

        // Handle ISO 8601 date strings (e.g., "2026-04-13T00:00:00")
        // Parse as UTC to avoid timezone offset issues
        if (typeof value === 'string') {
            // If it's an ISO string with T separator, parse carefully
            if (value.includes('T')) {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                    // Return a new date normalized to midnight UTC
                    return new Date(Date.UTC(
                        date.getUTCFullYear(),
                        date.getUTCMonth(),
                        date.getUTCDate()
                    ));
                }
            }
        }

        const date = new Date(value);
        return isNaN(date.getTime()) ? null : date;
    }

    /**
     * Check if two dates are the same (ignoring time)
     * Normalizes both dates to UTC midnight for comparison
     */
    private isSameDate(date1: Date, date2: Date): boolean {
        // Normalize both dates to UTC components to avoid timezone issues
        const year1 = date1.getUTCFullYear();
        const month1 = date1.getUTCMonth();
        const day1 = date1.getUTCDate();

        const year2 = date2.getUTCFullYear();
        const month2 = date2.getUTCMonth();
        const day2 = date2.getUTCDate();

        return year1 === year2 && month1 === month2 && day1 === day2;
    }

    /**
     * Validate filter rule
     */
    validateFilter(filter: IFilterRule): { valid: boolean; message?: string } {
        if (!filter.columnName) {
            return { valid: false, message: 'Column name is required' };
        }

        switch (filter.filterType) {
            case 'boolean':
                if (filter.value === null || filter.value === undefined) {
                    return { valid: false, message: 'Please select a value' };
                }
                break;

            case 'dateRange':
                if (!filter.dateFrom || !filter.dateTo) {
                    return { valid: false, message: 'Please select both start and end dates' };
                }
                if (filter.dateFrom > filter.dateTo) {
                    return { valid: false, message: 'Start date must be before end date' };
                }
                break;

            case 'date':
                if (!filter.value) {
                    return { valid: false, message: 'Please select a date' };
                }
                break;

            case 'text':
                if (!filter.value || filter.value.trim() === '') {
                    return { valid: false, message: 'Please enter a value' };
                }
                break;

            case 'number':
                if (filter.value === null || filter.value === undefined) {
                    return { valid: false, message: 'Please enter a number' };
                }
                break;
        }

        return { valid: true };
    }

    /**
     * Get default filter configs for common employee fields
     */
    getDefaultEmployeeFilterConfigs(): IFilterConfig[] {
        return [
            {
                name: 'isActive',
                displayName: 'Active Status',
                filterType: 'boolean',
                dataType: 'boolean'
            },
            {
                name: 'futureEmployee',
                displayName: 'Future Employee',
                filterType: 'boolean',
                dataType: 'boolean'
            },
            {
                name: 'userLoginToBeCreated',
                displayName: 'User Login Status',
                filterType: 'boolean',
                dataType: 'boolean'
            },
            {
                name: 'startDate',
                displayName: 'Start Date',
                filterType: 'dateRange',
                dataType: 'date'
            },
            {
                name: 'endDate',
                displayName: 'End Date',
                filterType: 'dateRange',
                dataType: 'date'
            },
            {
                name: 'dateOfBirth',
                displayName: 'Date of Birth',
                filterType: 'dateRange',
                dataType: 'date'
            },
            {
                name: 'firstName',
                displayName: 'First Name',
                filterType: 'text',
                dataType: 'string',
                placeholder: 'Enter first name...'
            },
            {
                name: 'lastName',
                displayName: 'Last Name',
                filterType: 'text',
                dataType: 'string',
                placeholder: 'Enter last name...'
            },
            {
                name: 'email',
                displayName: 'Email',
                filterType: 'text',
                dataType: 'string',
                placeholder: 'Enter email...'
            },
            {
                name: 'phone',
                displayName: 'Phone',
                filterType: 'text',
                dataType: 'string',
                placeholder: 'Enter phone...'
            }
        ];
    }
}
